import React, { useEffect, useState, useMemo, useRef } from 'react'
import classNames from 'classnames'
import genderNames from 'data/genders/messages'
import languageNames from 'data/languages/messages'
import productNames from 'data/products/messages'
import shortProductNames from 'data/products/messagesShort'
import { useParams, useNavigate, Link } from 'react-router-dom'
import useBreakpoint from 'hooks/useBreakpoint'
import {
  LoadingFull,
  Card,
  Tag,
  Button,
  Kbd,
  Chip
} from '@arnsbogroup/react-voicearchive'
import {
  FormattedMessage,
  FormattedDateParts,
  FormattedPlural,
  FormattedList,
  useIntl,
} from 'react-intl'
import Icon from 'components/Icon'
import {
  PieChart,
  Pie,
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
  Legend,
  Tooltip,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  RadarChart,
} from 'recharts'
import Waveform from 'components/Waveform'
import { useKeyboardShortcut } from '@arnsbogroup/react-voicearchive/lib/hooks'
import useApi from 'hooks/useApi'
import messages from './messages'
import exploreMessages from 'containers/ExplorePage/messages'
import DemoPlayButton from 'containers/DemoPlayButton'
import useCurrentUser from 'hooks/useCurrentUser'
import QR from 'components/QR'
import { intents, sortDemos } from 'contexts/ProjectContext'
import ChildVoiceCard from 'components/ChildVoiceCard'

const VoicePage = ({ project, voices, dispatch, loading, onFetchVoice }) => {
  const bp = useBreakpoint()
  const { roles = [], features = [] } = useCurrentUser()
  const uriRef = useRef(null)
  const [copied, setCopied] = useState(false)
  const [focusedDemoIndex, setFocusedDemoIndex] = useState(-1)
  const { formatMessage, locale } = useIntl()
  const navigate = useNavigate()
  const { voiceId } = useParams()
  const voiceIndex = voices.findIndex(({ sku }) => sku === voiceId)
  const voice = voices[voiceIndex];
  const prevVoice = voices[voiceIndex - 1] || null
  const nextVoice = voices[voiceIndex + 1] || null
  const refsByIndex = useMemo(() => {
    if (!voice?.demos) {
      return []
    }

    return voice.demos.map((_, i) => React.createRef(null))
  }, [voice?.demos?.length, voice?.id])

  useKeyboardShortcut(['Escape'], () => navigate('..'), {
    overrideSystem: true,
  })
  useKeyboardShortcut(
    ['ArrowLeft'],
    () => (prevVoice ? navigate(`../${prevVoice.sku}`) : navigate(-1)),
    { overrideSystem: true }
  )
  useKeyboardShortcut(
    ['ArrowRight'],
    () => (nextVoice ? navigate(`../${nextVoice.sku}`) : navigate(-1)),
    { overrideSystem: true }
  )
  useKeyboardShortcut(
    ['ArrowUp'],
    () => setFocusedDemoIndex((i) => (voice.demos[i - 1] ? i - 1 : i)),
    { overrideSystem: true }
  )
  useKeyboardShortcut(
    ['ArrowDown'],
    () => setFocusedDemoIndex((i) => (voice.demos[i + 1] ? i + 1 : i)),
    { overrideSystem: true }
  )
  useKeyboardShortcut(
    [' '],
    () => {
      if (focusedDemoIndex > -1 && refsByIndex[focusedDemoIndex]) {
        refsByIndex[focusedDemoIndex].current.click()
      }
    },
    { overrideSystem: true }
  )

  useKeyboardShortcut(
    ['Enter'],
    () => {
      if (
        project.voices[voice.sku] &&
        voice?.demos[focusedDemoIndex] &&
        project.voices[voice.sku].demos.find(
          ({ id }) => id === voice?.demos[focusedDemoIndex].id
        )
      ) {
        dispatch({
          type: 'REMOVE_DEMO',
          payload: { voice, demo: voice.demos?.[focusedDemoIndex] },
        })
      } else if (project.voices[voice.sku] && !voice?.demos[focusedDemoIndex]) {
        dispatch({ type: 'REMOVE_VOICE', payload: voice })
      } else {
        dispatch({
          type: 'ADD_VOICE',
          payload: {
            voice,
            demo: voice.demos?.[focusedDemoIndex] || undefined,
          },
        })
      }
    },
    { overrideSystem: true }
  )

  const [stats, statsLoading, errors, fetchStats] = useApi(null, {
    load: false,
  })
  const [reviews, reviewsLoading, reviewsErrors, fetchReviews] = useApi(null, {
    load: false,
  })
  const [syntheticVoice, syntheticVoiceLoading, syntheticVoiceErrors, fetchSyntheticVoice] = useApi(null, {
    load: false,
  })

  useEffect(() => {
    if (!voices.length) {
      onFetchVoice(voiceId)
    } else if (voices.findIndex(({ sku }) => sku === voiceId) == -1) {
      // voices does only contain a subset of voices as the user sees on the home page
      // so we need to fetch the voice if it's not in the list
      onFetchVoice(voiceId)
    }
  }, [voices])

  useEffect(() => {
    setFocusedDemoIndex(-1)
    setCopied(false)
    fetchStats(`/rest/v2/supplier/${voiceId}/stats`)
    fetchReviews(`/rest/v2/supplier/${voiceId}/reviews`)
    fetchSyntheticVoice(`/rest/v2/supplier/${voiceId}/synthetic-voice`)
  }, [voiceId])

  const selectedDemoIds =
    project.voices[voiceId]?.demos.map(({ id }) => id) || []

  const groupDemosByLanguage = (demos = [], voiceLanguages = []) => {
    const sorted = sortDemos(demos)

    const groups = sorted.reduce((acc, demo) => {
      const lang = demo.language || ''
      if (!acc[lang]) {
        acc[lang] = []
      }
      acc[lang].push(demo)
      return acc
    }, {})

    const orderedLanguages = [
      ...voiceLanguages.filter(lang => lang in groups),
      ...Object.keys(groups).filter(lang => !voiceLanguages.includes(lang)),
    ]

    return orderedLanguages.map(language => ({
      language,
      demos: groups[language],
    }))
  }

  const LanguageSection = ({ language, children }) => (
    <div className='my-2'>
      <h3 className="px-4 py-3 text-sm font-bold">
        {language
          ? language in languageNames
            ? formatMessage(languageNames[language])
            : language
          : <FormattedMessage {...messages.missingDemoProduct} />}
      </h3>
      <div className="border-y border-gray-300">
        {children}
      </div>
    </div>
  )

  const DemoRow = ({
    demo,
    index,
    voice,
    bp,
    refsByIndex,
    dispatch,
    isFocused,
    isSelected,
  }) => (
    <div
      className={classNames(
        'p-2 md:py-0 md:px-4 flex items-center justify-between md:space-x-3 w-full',
        {
          'bg-blue-50': isSelected && !isFocused,
          'bg-gray-100': isFocused,
        }
      )}
    >
      <div className="md:w-56 md:flex-shrink-0">
        <h3 className="text-sm">
          {demo.productSlug && demo.productSlug in productNames ? (
            <FormattedMessage {...productNames[demo.productSlug]} />
          ) : demo.productSlug ? (
            <i className="italic capitalize">
              {demo.productSlug.replace('-', ' ')}
            </i>
          ) : demo?.deprecatedTypeName ? (
            <span className="space-x-2">
              <Chip state="error">
                <FormattedMessage {...messages.custom} />
              </Chip>
              <span>{demo.deprecatedTypeName}</span>
            </span>
          ) : (
            <FormattedMessage {...messages.missingDemoProduct} />
          )}
          <span>{demo.voicekind}</span>
        </h3>

        <h4 className="text-xs py-1">
          {demo.dialectName && (
            <div>
              <span className="font-semibold"><FormattedMessage {...messages.dialect} />:</span> {demo.dialectName}
            </div>
          )}

          {demo.accentName && (
            <div>
              <span className="font-semibold"><FormattedMessage {...messages.accent} />:</span>{' '}
              {demo.accentName in languageNames
                ? <FormattedMessage {...languageNames[demo.accentName]} />
                : demo.accentName
              }
            </div>
          )}

          {demo.voiceage && <span>{demo.voiceage}</span>}
        </h4>
      </div>

      <Waveform
        key={demo.url}
        src={demo.url}
        className="hidden md:block flex-shrink"
      />

      {demo.duration !== '0' && (
        <span className="hidden md:inline text-sm text-gray-300">
          {demo.duration}
        </span>
      )}

      <div className="flex items-center space-x-1 flex-shrink-0">
        {!isSelected && (
          <Button
            variant="plain"
            size="sm"
            onClick={() =>
              dispatch({
                type: 'ADD_VOICE',
                payload: { voice, demo, intent: intents.DOWNLOAD },
              })
            }
            icon="download"
          >
            <span className="hidden sm:inline">
              <FormattedMessage {...messages.download} />
            </span>
          </Button>
        )}

        <DemoPlayButton
          ref={refsByIndex[index]}
          voice={voice}
          demo={demo}
          showProduct={false}
          component="VoicePageDemoPlay"
          iconOnly={bp === 'sm'}
        />

        {isSelected ? (
          <Button
            variant="plain"
            size="sm"
            onClick={() =>
              dispatch({ type: 'REMOVE_DEMO', payload: { voice, demo } })
            }
            icon="times"
          >
            <FormattedMessage {...messages.deselect} />
          </Button>
        ) : (
          <Button
            variant="plain"
            size="sm"
            onClick={() =>
              dispatch({ type: 'ADD_VOICE', payload: { voice, demo } })
            }
            icon="plus"
          >
            <FormattedMessage {...messages.select} />
          </Button>
        )}
      </div>
    </div>
  )

  const demos = useMemo(() => {
    const grouped = groupDemosByLanguage(
      voice?.demos,
      voice?.languages || []
    )

    let flatIndex = 0

    return grouped.map(group => (
      <LanguageSection
        key={group.language || 'no-language'}
        language={group.language}
      >
        <div className='divide-y divide-gray-300'>
          {group.demos.map(demo => {
            const index = flatIndex++

            return (
              <DemoRow
                key={demo.id}
                demo={demo}
                index={index}
                voice={voice}
                bp={bp}
                refsByIndex={refsByIndex}
                dispatch={dispatch}
                isFocused={focusedDemoIndex === index}
                isSelected={selectedDemoIds.includes(demo.id)}
              />
            )
          })}
        </div>
      </LanguageSection>
    ))
  }, [
    focusedDemoIndex,
    selectedDemoIds,
    voice?.demos,
    voice?.languages,
  ])

  if (!voice || loading) {
    return <LoadingFull />
  }

  const onCopy = () => {
    uriRef.current.select()
    document.execCommand('copy')
    setCopied(true)
  }

  return (
    <div className="h-full">
      <div className="hidden md:flex border-b border-gray-300 justify-between items-center pl-8 pr-2 py-3 text-sm shadow-md sticky top-0 left-0 z-30 bg-white">
        <Link to=".." className="w-1/3 space-x-1 text-tertiary font-medium">
          <Icon icon="angle-left" />
          <FormattedMessage {...messages.goBack} tagName="span" />
          <Kbd className="text-tertiary">Esc</Kbd>
        </Link>

        <div className="w-1/3 space-x-3 flex justify-center">
          {prevVoice && (
            <Link
              to={`../${prevVoice.sku}`}
              className="text-gray-600 space-x-1"
            >
              <Kbd>
                <Icon icon={['fas', 'long-arrow-alt-left']} />
              </Kbd>
              <span className="text-primary">{prevVoice.sku}</span>
            </Link>
          )}
          {nextVoice && (
            <Link
              to={`../${nextVoice.sku}`}
              className="text-gray-600 space-x-1"
            >
              <span className="text-primary">{nextVoice.sku}</span>
              <Kbd>
                <Icon icon={['fas', 'long-arrow-alt-right']} />
              </Kbd>
            </Link>
          )}
        </div>

        <div className="w-1/3 space-x-2 px-4 text-right">
          <span className="text-gray-600">
            <FormattedMessage {...messages.selectKeyboardShortcut} />
          </span>
          <Kbd>Enter</Kbd>

          <span className="text-gray-600">
            <FormattedMessage {...messages.demosKeyboardShortcuts} />
          </span>
          <Kbd>
            <Icon icon={['fas', 'long-arrow-alt-up']} />
          </Kbd>
          <Kbd>
            <Icon icon={['fas', 'long-arrow-alt-down']} />
          </Kbd>
          <Kbd>Space</Kbd>
        </div>
      </div>

      {/* min-h-full, not h-full: h-full pins the row to the viewport, so the
          divide-x border stopped one screen down while the demo list kept
          going. min-h-full still fills the screen when a voice has few demos. */}
      <div className="flex flex-col md:flex-row divide-x divide-gray-300 min-h-full">
        <div className="w-full md:w-1/3">
          <div className="p-4 md:p-8 space-y-4 md:space-y-8">
            <div className="flex">
              <div className="w-full">
                <div className="space-y-3">
                  <Link to=".." className="space-x-1 text-primary font-medium text-sm md:hidden">
                    <Icon icon="angle-left" />
                    <FormattedMessage {...messages.goBack} tagName="span" />
                  </Link>

                  {voice?.age < 18 &&
                    <ChildVoiceCard/>
                  }

                  <div className="space-y-1">
                    {voice.featured && (
                      <h2 className="block text-sm font-medium text-tertiary">
                        Recommended <Icon icon={['fas', 'star']} />
                      </h2>
                    )}

                    <h1 className="text-xl font-medium">{voice.sku}</h1>
                  </div>

                  <h3 className="block text-base text-gray-700">
                      <FormattedMessage
                        {...messages.nativeIn}
                        tagName="span"
                        values={{
                          age: voice.age,
                          gender: (
                            <FormattedMessage {...genderNames[voice.gender]} />
                          ),
                          languages: (
                            <span>
                              <FormattedList
                                type="conjunction"
                                value={(voice.languages || []).map((langTag) =>
                                  langTag in languageNames ? formatMessage(languageNames[langTag]) : langTag
                                )}
                              />
                            </span>
                          ),
                          hasSkills: !!(voice.skills || []).length,
                          skills: (
                            <FormattedList
                              type="conjunction"
                              value={(voice.skills || []).map((skill, i) => (
                                <a
                                  className="lowercase border-b border-dotted border-secondary-darker"
                                  title={formatMessage(
                                    messages[`${skill.toLowerCase()}Hint`]
                                  )}
                                  target="_blank"
                                  href={formatMessage(
                                    messages[`${skill.toLowerCase()}Href`]
                                  )}
                                >
                                  <FormattedMessage
                                    {...messages[skill.toLowerCase()]}
                                  />
                                </a>
                              ))}
                            />
                          ),
                        }}
                      />
                  </h3>

                  { !!voice?.additionalLanguages?.length &&
                    <h3 className="block font-medium text-base text-gray-700">
                      <FormattedMessage
                        {...messages.additionalLanguages}
                        values={{
                          languages: (
                            <span className="font-normal">
                              <FormattedList
                                type="conjunction"
                                value={voice.additionalLanguages.map((langTag) =>
                                  langTag in languageNames ? formatMessage(languageNames[langTag]) : langTag
                                )}
                              />
                            </span>
                          ),
                        }}
                      />
                    </h3>
                  }

                  { roles.includes('coordinator') &&
                    voice?.otherLanguages?.map(({ proficiency, languages }) => (
                      <h3
                        key={proficiency}
                        className="block font-medium text-base text-gray-700"
                      >
                        {exploreMessages[`languagesProficiency.${proficiency}`] ? (
                          <FormattedMessage
                            {...exploreMessages[`languagesProficiency.${proficiency}`]}
                          />
                        ) : (
                          <span className="capitalize">{proficiency.replace('-', ' ')}</span>
                        )}
                        {': '}
                        <span className="font-normal">
                          <FormattedList
                            type="conjunction"
                            value={languages.map((langTag) =>
                              langTag in languageNames ? formatMessage(languageNames[langTag]) : langTag
                            )}
                          />
                        </span>
                      </h3>
                    ))
                  }

                  { !!voice?.references?.length && roles.includes('coordinator') &&
                    <h3 className="block text-base text-gray-700">
                      <FormattedMessage
                        {...messages.experienceWithBrands}
                        values={{
                          brands: (
                            <span className="">
                              <FormattedList value={voice.references} />
                            </span>
                          ),
                        }}
                      />
                    </h3>
                  }

                  {voice &&
                    voice.descriptions &&
                    voice.descriptions[locale.toLowerCase()] && (
                      <p className="text-base whitespace-pre-line text-gray-700">
                        {voice.descriptions[locale.toLowerCase()]}
                      </p>
                    )}

                  { !!voice.tags.length && roles.includes('coordinator') &&
                    <div className="space-x-1">
                      {voice.tags?.map((tag) => (
                        <span key={tag} className="text-primary">#{tag}</span>
                      ))}
                    </div>
                  }
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex space-x-2">
                <div className="w-1/2 md:w-full 2xl:w-1/3">
                  {project.voices[voice.sku] ? (
                    <Button
                      onClick={() =>
                        dispatch({ type: 'REMOVE_VOICE', payload: voice })
                      }
                      className="w-full"
                    >
                      <Icon icon="times" /> <FormattedMessage id='.remove' defaultMessage='Remove'/>
                    </Button>
                  ) : (
                    <Button
                      onClick={() =>
                        dispatch({ type: 'ADD_VOICE', payload: { voice } })
                      }
                      className="w-full"
                    >
                      <Icon icon="plus" /> <FormattedMessage {...messages.select}/>
                    </Button>
                  )}
                </div>
                <div className="w-1/2 md:w-full 2xl:w-1/3">
                  <Button
                    variant="outline"
                    className="w-full space-x-1"
                    onClick={onCopy}
                  >
                    <Icon icon="external-link" className="mr-1" />
                    <FormattedMessage
                      {...messages[copied ? 'copiedToClipboard' : 'share']}
                      tagName="span"
                    />
                  </Button>
                </div>
              </div>

              <div className="bg-gray-100 p-2 text-sm grid grid-cols-6 border border-gray-300 mt-2 text-sm">
                <div className="cursor-pointer col-span-5">
                  <input
                    type="uri"
                    readOnly
                    value={window?.location.href || ''}
                    className="w-full h-auto bg-transparent text-gray-700"
                    ref={uriRef}
                    onClick={() => uriRef.current.select()}
                  />
                </div>
                <div className="text-right cursor-pointer hover:text-primary">
                  <Icon icon={copied ? 'check' : 'share'} onClick={onCopy} />
                </div>
              </div>
            </div>

            {voice.note && (
              <Card className="bg-yellow-100 p-4 text-sm">{voice.note}</Card>
            )}

            {Array.isArray(reviews) && !!reviews?.length && (
              <Card className="p-4 shadow-sm space-y-4">
                <h1 className="font-bold">
                  <FormattedMessage {...messages.reviews} />
                </h1>
                <div className="space-y-2 text-sm">
                  {reviews?.filter(
                      ({ rating }) =>
                        rating > (roles.includes('coordinator') ? 0 : 4)
                    )?.map((review) => (
                      <div
                        key={review.id}
                        className="flex items-center space-x-4"
                      >
                        <Icon icon="user" />
                        <div>
                          <p>{review.body}</p>
                          <div className="flex justify-between items-center mt-2">
                            <div>
                              {[...Array(Math.round(review.rating))].map(
                                (_, i) => (
                                  <Icon
                                    className={
                                      review.rating > 4.5
                                        ? 'text-positive'
                                        : 'text-gray-300'
                                    }
                                    key={`${review.id}-star-${i}`}
                                    icon={['fas', 'star']}
                                  />
                                )
                              )}
                            </div>
                            {roles.includes('coordinator') && (
                              <div className="text-gray-600">
                                - {review.created_by}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </Card>
            )}

            <Card className="md:hidden shadow-sm divide-y divide-gray-300">{demos}</Card>

            { stats?.jobsTotal > 0 &&
              <Card className="p-4 shadow-sm space-y-4">
                <h1 className="font-bold">
                  <FormattedMessage {...messages.experience} />
                </h1>

                <div className="grid grid-cols-2 gap-2 text-gray-700">
                  {[
                    ['jobsTotal', 'comments'],
                    ['minutesTotal', 'microphone-alt'],
                  ].map(([key, icon]) => (
                    <div key={key} className="space-x-2">
                      <Icon icon={icon} />
                      <span>
                        <FormattedMessage
                          {...messages[key]}
                          values={{ count: stats?.[key] || 'N/A' }}
                        />
                      </span>
                    </div>
                  ))}



                  <div className="space-x-2">
                    <Icon icon="user" />
                    <span className="space-x-1">
                      <FormattedMessage {...messages.joinedOn} tagName="span" />
                      <FormattedDateParts
                        value={voice.created}
                        year="numeric"
                        month="long"
                      >
                        {(parts) => (
                          <span className="font-medium">
                            {parts[0].value} {parts[2].value}
                          </span>
                        )}
                      </FormattedDateParts>
                    </span>
                  </div>

                  <div className="space-x-2">
                    <Icon icon="heart" />
                    <span>
                      <FormattedPlural
                        value={stats?.sharesTotal || 0}
                        one={
                          <FormattedMessage
                            {...messages.sharesTotalOne}
                            values={{ count: stats?.sharesTotal || 0 }}
                          />
                        }
                        other={
                          <FormattedMessage
                            {...messages.sharesTotalOther}
                            values={{ count: stats?.sharesTotal || 0 }}
                          />
                        }
                      />
                    </span>
                  </div>
                </div>

                { voice.experience &&
                  <ResponsiveContainer width="100%" aspect={2}>
                    <PieChart isAnimationActive={false}>
                      <Pie
                        isAnimationActive={false}
                        data={Object.entries(voice.experience)
                          .filter(([, value]) => value > 0)
                          .map(([name, value], i) => ({
                            name,
                            value,
                            fill: [
                              // '#eff6ff',
                              // '#dbeafe',
                              '#bfdbfe',
                              '#93c5fd',
                              '#60a5fa',
                              '#3b82f6',
                              '#2563eb',
                              '#1d4ed8',
                              '#1e40af',
                              '#1e3a8a',
                            ][i],
                          }))}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        label={({ x, y, cx, name }) => (
                          <text
                            x={x > cx ? x + 6 : x - 6}
                            y={y}
                            fill="black"
                            textAnchor={x > cx ? 'start' : 'end'}
                            dominantBaseline="central"
                            className="text-xs"
                          >
                            {
                              name in shortProductNames ?
                                formatMessage(shortProductNames[name]) :
                                productNames[name].slice(0, 26).concat('...')
                            }
                          </text>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                }
              </Card>
            }

            { voice.studio?.length > 0 && roles.includes('coordinator') &&
              <Card className="p-4 shadow-sm space-y-4">
                <h1 className="font-bold">
                  <FormattedMessage {...messages.studio} />
                </h1>
                <p className="text-sm">{voice.studio}</p>
              </Card>
            }

            { (syntheticVoice != null && syntheticVoice != 'No supplier found') ? syntheticVoice.map((syntheticVoice, i) => (
              <>
              { syntheticVoice.approved === "1" &&
              <Card key={'synvoice'+i} className="p-4 shadow-sm space-y-4">
                { syntheticVoice.supplier_demo_url && syntheticVoice.synthetic_demo_url &&
                <>
                <h1 className="font-bold">
                  <FormattedMessage id='a' defaultMessage='Compare with synthetic voice' />
                </h1>

                <p className="text-sm">
                <FormattedMessage {...messages.demoVoiceLabel} />
                </p>
                <div className="flex">
                  <Waveform src={syntheticVoice.supplier_demo_url} />

                  <div className="flex space-x-1">
                    <DemoPlayButton
                      childClassName=""
                      demo={{ url: syntheticVoice.supplier_demo_url }}
                    />
                  </div>
                </div>

                <p className="text-sm">
                <FormattedMessage {...messages.syntheticVoiceLabel} />
                </p>
                <div className="flex">
                  <Waveform src={syntheticVoice.synthetic_demo_url} />

                  <div className="flex space-x-1">
                    <DemoPlayButton
                      childClassName=""
                      demo={{ url: syntheticVoice.synthetic_demo_url }}
                    />
                  </div>
                </div>
                </>
                }

                <Button
                  variant="outline"
                  state="positive"
                  className="w-full space-x-1"
                  onClick={() => {
                    navigate(`../ai/${voice.sku}`)
                  }}
                >
                  <Icon icon="robot" className="mr-1" />
                  <FormattedMessage
                    {...messages.tryvoice}
                    tagName="span"
                  />
                </Button>

              </Card>
              }
              </>
            )) : null}

          </div>
        </div>

        <div className="md:w-2/3 flex flex-col">
          <div className="hidden md:block">
            { voice?.demos.length > 2 &&
              <div className="flex justify-between items-center p-2 md:py-0 md:px-4">
                <span className="text-xs text-gray-600 font-medium"><FormattedMessage {...messages.demoAmountForVoicetalent}
                                                                                      values={{amount: voice?.demos.length}}/>
          </span>

                <div className="space-x-2">
                  { selectedDemoIds.length > 0 &&
                    <Button
                      size="xs"
                      variant="plain"
                      onClick={() => dispatch({ type: 'REMOVE_ALL_DEMOS', payload: { voice } })}
                    >
                      <FormattedMessage id='components.VoicePage.deselectAllDemos' defaultMessage='Deselect all'/>
                    </Button>
                  }

                  <Button
                    size="xs"
                    variant="plain"
                    onClick={() => dispatch({ type: 'ADD_ALL_DEMOS', payload: { voice } })}
                  >
                    <FormattedMessage id='components.VoicePage.selectAllDemos' defaultMessage='Select all'/>
                  </Button>
                </div>
              </div>
            }

            { demos }
          </div>

          {voice.featured && (
            <div className="hidden md:flex justify-center items-center py-32">
              <div className="border border-dashed border-gray-300 rounded-md p-4 space-y-3 w-1/3 text-gray-600">
                <h1 className="font-medium text-base">
                  <FormattedMessage
                    {...messages.customDemoOfferTitle}
                    values={{
                      sku: voice.sku,
                      b: (chunk) => (
                        <b className="font-bold text-tertiary">{chunk}</b>
                      ),
                    }}
                  />
                </h1>
                <p className="text-sm">
                  <FormattedMessage
                    {...messages.customDemoOfferBody}
                    values={{
                      href: (chuck) => (
                        <button
                          className="font-bold text-primary"
                          onClick={() =>
                            dispatch({ type: 'ADD_VOICE', payload: { voice } })
                          }
                        >
                          {chuck}
                        </button>
                      ),
                      sku: voice.sku,
                    }}
                  />
                </p>

                <Button
                  className="truncate w-full"
                  state="tertiary"
                  variant="outline"
                  onClick={() =>
                    dispatch({ type: 'ADD_VOICE', payload: { voice } })
                  }
                >
                  <span className="lg:hidden">
                    <FormattedMessage id='components.VoicePage.requestDemo' defaultMessage='Request demo'/>
                  </span>
                  <span className="hidden lg:inline">
                    <FormattedMessage id='components.VoicePage.requestFreeDemoFromVT' defaultMessage='Get free demo from {sku}' values={{sku:voice.sku}}/>
                  </span>
                </Button>
              </div>
            </div>
          )}

          <div
            className={classNames('p-4 md:hidden', {
              'mb-48': project.isScoped,
            })}
          >
            {project.voices[voice.sku] ? (
              <Button
                onClick={() =>
                  dispatch({ type: 'REMOVE_VOICE', payload: voice })
                }
                className="w-full"
              >
                <Icon icon="times" />
                <FormattedMessage id="components.VoicePage.removeVTVoicesFromProject" defaultMessage='Remove'/>
              </Button>
            ) : (
              <Button
                onClick={() =>
                  dispatch({ type: 'ADD_VOICE', payload: { voice } })
                }
                className="w-full"
              >
                <Icon icon="plus" />
                <FormattedMessage id="components.VoicePage.addVTVoicesFromProject" defaultMessage='Select'/>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default VoicePage
